
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import {
  projectVideo,
  projectDetails,
  mapData,
  partners,
} from '../../mocks/projectData';
import { fetchWarmStories, fetchPublicFundOverview, type WarmStoryItem, type FundOverviewData } from '../../api/dripPlan';

const SOURCE_COLORS = ['#2A7A62', '#F4B942', '#E06B3D', '#3A9A82', '#1677ff', '#722ed1'];

const ProjectPage = () => {
  const [activeTab, setActiveTab] = useState<'recipients' | 'donors' | 'volunteers'>('recipients');
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  const [selectedStory, setSelectedStory] = useState<any>(null);
  const [likedStories, setLikedStories] = useState<number[]>([]);
  const [storyLikeCounts, setStoryLikeCounts] = useState<Record<number, number>>({});
  const [fundOverview, setFundOverview] = useState<FundOverviewData | null>(null);
  const [fundLoading, setFundLoading] = useState(true);
  const [warmStories, setWarmStories] = useState<Record<'recipients' | 'donors' | 'volunteers', WarmStoryItem[]>>({
    recipients: [],
    donors: [],
    volunteers: [],
  });

  const [warmStoriesLoading, setWarmStoriesLoading] = useState(true);
  const tabConfig = {
    recipients: { label: '受助者故事', data: warmStories.recipients },
    donors: { label: '捐赠者心声', data: warmStories.donors },
    volunteers: { label: '志愿者日记', data: warmStories.volunteers },
  };

  useEffect(() => {
    setFundLoading(true);
    fetchPublicFundOverview()
      .then((data) => setFundOverview(data))
      .finally(() => setFundLoading(false));
  }, []);

  useEffect(() => {
    setWarmStoriesLoading(true);
    Promise.all([
      fetchWarmStories('recipients'),
      fetchWarmStories('donors'),
      fetchWarmStories('volunteers'),
    ])
      .then(([r, d, v]) => {
        setWarmStories({
          recipients: Array.isArray(r) ? r : [],
          donors: Array.isArray(d) ? d : [],
          volunteers: Array.isArray(v) ? v : [],
        });
      })
      .catch(() => {
        setWarmStories({ recipients: [], donors: [], volunteers: [] });
      })
      .finally(() => setWarmStoriesLoading(false));
  }, []);

  const activities = [
    { month: '2月', name: '项目启动', icon: 'ri-rocket-line', color: '#2A7A62' },
    { month: '3月', name: '春季送水', icon: 'ri-sun-line', color: '#F4B942' },
    { month: '4月', name: '夏季送水', icon: 'ri-temp-hot-line', color: '#E06B3D' },
    { month: '6月', name: '高温关怀', icon: 'ri-fire-line', color: '#E06B3D' },
    { month: '8月', name: '秋季送水', icon: 'ri-leaf-line', color: '#2A7A62' },
    { month: '10月', name: '重阳敬老', icon: 'ri-heart-line', color: '#F4B942' },
    { month: '12月', name: '暖冬行动', icon: 'ri-snowy-line', color: '#2A7A62' },
  ];

  const handleStoryLike = (storyId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (likedStories.includes(storyId)) {
      setLikedStories(likedStories.filter(id => id !== storyId));
      setStoryLikeCounts(prev => ({
        ...prev,
        [storyId]: (prev[storyId] || 100) - 1
      }));
    } else {
      setLikedStories([...likedStories, storyId]);
      setStoryLikeCounts(prev => ({
        ...prev,
        [storyId]: (prev[storyId] || 100) + 1
      }));
    }
  };

  const getLikeCount = (storyId: number, defaultCount?: number) => {
    if (storyLikeCounts[storyId] != null) return storyLikeCounts[storyId];
    if (defaultCount != null) return defaultCount;
    return Math.floor(Math.random() * 200) + 50;
  };

  const donationSources = (fundOverview?.donationSourceBreakdown?.length
    ? fundOverview.donationSourceBreakdown
    : [{ name: '暂无数据', value: 0, percentage: 0 }]
  ).map((item, index) => ({
    ...item,
    color: SOURCE_COLORS[index % SOURCE_COLORS.length],
  }));

  const monthlyData = fundOverview?.monthlyBottlesTrend?.length
    ? fundOverview.monthlyBottlesTrend.map((item) => ({ month: item.month, bottles: item.bottles }))
    : [{ month: '暂无', bottles: 0 }];

  const maxMonthlyBottles = Math.max(...monthlyData.map((d) => d.bottles), 1);
  const currentMonthBottles = monthlyData.length ? monthlyData[monthlyData.length - 1].bottles : 0;

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <Navbar />

      {/* Hero Video Section */}
      <section className="mt-20 relative h-[400px] md:h-[600px] overflow-hidden">
        <img
          src={projectVideo.poster}
          alt={projectVideo.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-[1400px] mx-auto">
            <h1 className="text-2xl md:text-5xl font-bold text-white mb-2 md:mb-4">点滴公益 · 让爱心汇聚成河</h1>
            <p className="text-base md:text-xl text-white/90">用透明公益,传递城市温度</p>
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-2 gap-4 md:gap-8">
            {projectDetails.map((detail, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#F9F9F9] to-white p-4 md:p-8 rounded-xl md:rounded-2xl border border-[#E0E0E0] hover:border-[#2A7A62] hover:shadow-xl transition-all"
              >
                <div className="flex flex-col md:flex-row items-start gap-3 md:gap-4">
                  <div className="w-8 h-8 md:w-14 md:h-14 flex items-center justify-center bg-gradient-to-br from-[#2A7A62] to-[#3A9A82] rounded-lg md:rounded-xl flex-shrink-0">
                    <i className={`${detail.icon} text-base md:text-2xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm md:text-2xl font-bold text-[#1E1E1E] mb-1 md:mb-4">{detail.title}</h3>
                    <p className="text-[10px] md:text-base text-[#6C6C6C] leading-relaxed">{detail.content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Execution Flow */}
          <div className="mt-8 md:mt-16 bg-gradient-to-r from-[#E8F4F0] to-[#F9F9F9] rounded-2xl md:rounded-3xl p-6 md:p-12">
            <h3 className="text-xl md:text-3xl font-bold text-[#2A7A62] mb-6 md:mb-8 text-center">执行流程</h3>
            <div className="grid grid-cols-3 md:flex md:flex-wrap md:justify-center gap-3 md:gap-4">
              {[
                '接收捐赠',
                '统一采购',
                '确认需求',
                '志愿者配送',
                '签收确认',
                '公示记录',
                '发布报告',
              ].map((step, index) => (
                <div key={index} className="flex flex-col md:flex-row items-center">
                  <div className="flex flex-col items-center gap-2 bg-white px-3 py-3 md:px-6 md:py-4 rounded-full shadow-md w-full md:w-auto">
                    <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-[#2A7A62] text-white rounded-full font-bold text-xs md:text-sm flex-shrink-0">
                      {index + 1}
                    </div>
                    <span className="text-[10px] md:text-base font-medium text-[#1E1E1E] text-center">{step}</span>
                  </div>
                  {index < 6 && (
                    <i className="ri-arrow-down-line md:ri-arrow-right-line text-lg md:text-2xl text-[#2A7A62] my-1 md:my-0 md:mx-2 hidden md:block"></i>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Real-time Love Map */}
      <section className="py-12 md:py-20 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto px-6">
          <h2 className="text-2xl md:text-4xl font-bold text-[#2A7A62] text-center mb-2 md:mb-4">实时爱心地图</h2>
          <p className="text-center text-sm md:text-base text-[#6C6C6C] mb-6 md:mb-12">点击区域查看详细发放信息</p>

          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-6 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {mapData.districts.map((district) => (
                <div
                  key={district.name}
                  onClick={() => setSelectedDistrict(selectedDistrict === district.name ? null : district.name)}
                  className={`bg-gradient-to-br from-[#F9F9F9] to-white rounded-lg md:rounded-xl p-4 md:p-6 hover:shadow-xl transition-all cursor-pointer border-2 ${
                    selectedDistrict === district.name ? 'border-[#2A7A62] shadow-xl' : 'border-transparent'
                  }`}
                  style={{ 
                    borderLeftWidth: '4px',
                    borderLeftColor: district.color,
                  }}
                >
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <span className="font-bold text-[#1E1E1E] text-sm md:text-lg">{district.name}</span>
                    <i className="ri-map-pin-fill text-lg md:text-2xl" style={{ color: district.color }}></i>
                  </div>
                  <p className="text-xl md:text-3xl font-bold text-[#2A7A62] mb-1">
                    {district.bottles.toLocaleString()}
                  </p>
                  <p className="text-xs md:text-sm text-[#6C6C6C]">已发放瓶数</p>
                  
                  {selectedDistrict === district.name && (
                    <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-[#E0E0E0] space-y-2">
                      <div className="flex items-center justify-between text-xs md:text-sm">
                        <span className="text-[#6C6C6C]">覆盖街道</span>
                        <span className="font-bold text-[#1E1E1E]">{Math.floor(district.bottles / 5000)}个</span>
                      </div>
                      <div className="flex items-center justify-between text-xs md:text-sm">
                        <span className="text-[#6C6C6C]">受益人数</span>
                        <span className="font-bold text-[#1E1E1E]">{Math.floor(district.bottles / 12)}人</span>
                      </div>
                      <div className="flex items-center justify-between text-xs md:text-sm">
                        <span className="text-[#6C6C6C]">本月新增</span>
                        <span className="font-bold text-[#2A7A62]">+{Math.floor(district.bottles * 0.08)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Map Legend */}
            <div className="mt-6 md:mt-8 flex items-center justify-center gap-4 md:gap-8 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-[#2A7A62]"></div>
                <span className="text-xs md:text-sm text-[#6C6C6C]">高发放量 (&gt;80K)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-[#F4B942]"></div>
                <span className="text-xs md:text-sm text-[#6C6C6C]">中发放量 (40K-80K)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-[#E06B3D]"></div>
                <span className="text-xs md:text-sm text-[#6C6C6C]">待提升 (&lt;40K)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Annual Activity Calendar */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <h2 className="text-2xl md:text-4xl font-bold text-[#2A7A62] text-center mb-2 md:mb-4">年度活动日历</h2>
          <p className="text-center text-sm md:text-base text-[#6C6C6C] mb-6 md:mb-12">全年不间断的爱心传递</p>

          <div className="relative">
            {/* Timeline Container */}
            <div className="overflow-x-auto pb-6 md:pb-8">
              <div className="flex items-center gap-4 md:gap-8 min-w-max px-4">
                {activities.map((activity, index) => (
                  <div key={index} className="flex items-center">
                    {/* Activity Card */}
                    <div className="flex flex-col items-center">
                      <div 
                        className="w-16 h-16 md:w-24 md:h-24 flex items-center justify-center rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-110"
                        style={{ backgroundColor: activity.color }}
                      >
                        <i className={`${activity.icon} text-2xl md:text-4xl text-white`}></i>
                      </div>
                      <div className="mt-3 md:mt-4 text-center">
                        <p className="text-sm md:text-lg font-bold text-[#1E1E1E]">{activity.month}</p>
                        <p className="text-xs md:text-sm text-[#6C6C6C] mt-1">{activity.name}</p>
                      </div>
                    </div>

                    {/* Connector Line */}
                    {index < activities.length - 1 && (
                      <div className="w-12 md:w-16 h-1 bg-gradient-to-r from-[#2A7A62] to-[#F4B942] mx-2 md:mx-4"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Scroll Hint */}
            <div className="flex items-center justify-center gap-2 text-[#6C6C6C] text-xs md:text-sm mt-4">
              <i className="ri-arrow-left-right-line"></i>
              <span>左右滑动查看更多活动</span>
            </div>
          </div>

          {/* Activity Stats */}
          <div className="mt-8 md:mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            <div className="bg-gradient-to-br from-[#2A7A62] to-[#3A9A82] rounded-xl md:rounded-2xl p-4 md:p-6 text-white text-center">
              <i className="ri-calendar-check-line text-2xl md:text-4xl mb-2 md:mb-3"></i>
              <p className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">365</p>
              <p className="text-xs md:text-sm opacity-90">全年服务天数</p>
            </div>
            <div className="bg-gradient-to-br from-[#F4B942] to-[#E8A832] rounded-xl md:rounded-2xl p-4 md:p-6 text-white text-center">
              <i className="ri-team-line text-2xl md:text-4xl mb-2 md:mb-3"></i>
              <p className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">1,200+</p>
              <p className="text-xs md:text-sm opacity-90">志愿者参与</p>
            </div>
            <div className="bg-gradient-to-br from-[#E06B3D] to-[#D05B2D] rounded-xl md:rounded-2xl p-4 md:p-6 text-white text-center">
              <i className="ri-map-pin-line text-2xl md:text-4xl mb-2 md:mb-3"></i>
              <p className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">500+</p>
              <p className="text-xs md:text-sm opacity-90">服务点位</p>
            </div>
            <div className="bg-gradient-to-br from-[#2A7A62] to-[#1E5A4A] rounded-xl md:rounded-2xl p-4 md:p-6 text-white text-center">
              <i className="ri-heart-line text-2xl md:text-4xl mb-2 md:mb-3"></i>
              <p className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">50K+</p>
              <p className="text-xs md:text-sm opacity-90">受益人次</p>
            </div>
          </div>
        </div>
      </section>

      {/* Data Dashboard */}
      <section className="py-12 md:py-20 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto px-6">
          <h2 className="text-2xl md:text-4xl font-bold text-[#2A7A62] text-center mb-6 md:mb-12">实时数据看板</h2>

          {fundLoading ? (
            <div className="text-center text-[#6C6C6C] py-12">加载数据中...</div>
          ) : (
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl p-6 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              {/* Charts Section */}
              <div className="space-y-6 md:space-y-8">
                {/* Donation Sources */}
                <div>
                  <h3 className="text-lg md:text-2xl font-bold text-[#1E1E1E] mb-4 md:mb-6">捐赠来源分布</h3>
                  <div className="space-y-3 md:space-y-4">
                    {donationSources.map((source) => (
                      <div key={source.name}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm md:text-base font-medium text-[#1E1E1E]">
                            {source.name}
                          </span>
                          <span className="text-base md:text-lg font-bold text-[#2A7A62]">{source.percentage}%</span>
                        </div>
                        <div className="h-2 md:h-3 bg-[#E0E0E0] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-1000"
                            style={{
                              width: `${source.percentage}%`,
                              backgroundColor: source.color,
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Stats */}
                <div className="grid grid-cols-3 gap-3 md:gap-4">
                  <div className="bg-gradient-to-br from-[#2A7A62] to-[#3A9A82] rounded-lg md:rounded-xl p-3 md:p-4 text-white text-center">
                    <p className="text-xl md:text-3xl font-bold">{fundOverview?.regionCount ?? 0}</p>
                    <p className="text-xs md:text-sm mt-1">覆盖区县</p>
                  </div>
                  <div className="bg-gradient-to-br from-[#F4B942] to-[#E8A832] rounded-lg md:rounded-xl p-3 md:p-4 text-white text-center">
                    <p className="text-xl md:text-3xl font-bold">{currentMonthBottles.toLocaleString()}</p>
                    <p className="text-xs md:text-sm mt-1">本月发放(瓶)</p>
                  </div>
                  <div className="bg-gradient-to-br from-[#E06B3D] to-[#D05B2D] rounded-lg md:rounded-xl p-3 md:p-4 text-white text-center">
                    <p className="text-xl md:text-3xl font-bold">{(fundOverview?.totalBottlesDisbursed ?? 0).toLocaleString()}</p>
                    <p className="text-xs md:text-sm mt-1">累计发放(瓶)</p>
                  </div>
                </div>
              </div>

              {/* Monthly Distribution */}
              <div>
                <h3 className="text-lg md:text-2xl font-bold text-[#1E1E1E] mb-4 md:mb-6">月度发放趋势</h3>
                <div className="flex items-end justify-between gap-2 md:gap-3 h-48 md:h-64 px-2">
                  {monthlyData.map((data) => (
                    <div key={data.month} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-full bg-gradient-to-t from-[#2A7A62] to-[#3A9A82] rounded-t-lg hover:from-[#1E5A4A] hover:to-[#2A7A62] transition-all cursor-pointer relative group"
                           style={{ height: `${(data.bottles / maxMonthlyBottles) * 100}%`, minHeight: '20px' }}>
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#1E1E1E] text-white text-[10px] md:text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          {data.bottles.toLocaleString()}瓶
                        </div>
                      </div>
                      <span className="text-[10px] md:text-sm text-[#6C6C6C] font-medium">{data.month}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Stories Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <h2 className="text-2xl md:text-4xl font-bold text-[#2A7A62] text-center mb-6 md:mb-12">温暖故事</h2>

          {/* Tab Switcher */}
          <div className="flex justify-center mb-8 md:mb-12 overflow-x-auto">
            <div className="inline-flex bg-[#F9F9F9] rounded-full p-1">
              {(Object.keys(tabConfig) as Array<keyof typeof tabConfig>).map((key) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-4 py-2 md:px-8 md:py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap text-sm md:text-base ${
                    activeTab === key
                      ? 'bg-[#2A7A62] text-white shadow-lg'
                      : 'text-[#6C6C6C] hover:text-[#2A7A62]'
                  }`}
                >
                  {tabConfig[key].label}
                </button>
              ))}
            </div>
          </div>

          {/* Stories Grid - 仅展示接口返回的真实数据，不展示 mock */}
          {warmStoriesLoading ? (
            <div className="text-center py-12 text-[#6C6C6C]">加载中...</div>
          ) : tabConfig[activeTab].data.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-[#6C6C6C] mb-2">该分类暂无温暖故事</p>
              <p className="text-sm text-[#6C6C6C]">请在管理端「动态发布」中添加内容，并将展示位置选为「温暖故事」及对应分类（受助者/捐赠者/志愿者）</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {tabConfig[activeTab].data.map((item) => (
                <div
                  key={item.id}
                  onClick={() => setSelectedStory(item)}
                  className="bg-[#F9F9F9] rounded-xl md:rounded-2xl p-4 md:p-6 hover:shadow-xl transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-[#2A7A62]"
                    />
                    <div>
                      <h4 className="text-sm md:text-lg font-bold text-[#1E1E1E]">{item.name}</h4>
                      <p className="text-xs md:text-sm text-[#6C6C6C]">{item.role}</p>
                    </div>
                  </div>
                  <p className="text-xs md:text-base text-[#1E1E1E] leading-relaxed mb-3 md:mb-4 line-clamp-3">{item.story}</p>
                  <div className="flex items-center justify-between text-xs md:text-sm text-[#6C6C6C]">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {item.date}
                    </span>
                    <button
                      onClick={(e) => handleStoryLike(item.id, e)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full transition-all cursor-pointer ${
                        likedStories.includes(item.id)
                          ? 'bg-[#E06B3D]/10 text-[#E06B3D]'
                          : 'hover:bg-[#E06B3D]/10 hover:text-[#E06B3D]'
                      }`}
                    >
                      <i className={`${likedStories.includes(item.id) ? 'ri-heart-fill' : 'ri-heart-line'} text-base md:text-lg`}></i>
                      <span>{getLikeCount(item.id, item.likeCount)}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-12 md:py-20 bg-[#F9F9F9]">
        <div className="max-w-[1400px] mx-auto px-6">
          <h2 className="text-2xl md:text-4xl font-bold text-[#2A7A62] text-center mb-6 md:mb-12">合作伙伴</h2>
          <div className="grid grid-cols-4 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-6">
            {partners.map((partner, index) => (
              <div
                key={index}
                className="bg-white rounded-lg md:rounded-xl p-3 md:p-6 hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br from-[#2A7A62] to-[#3A9A82] rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-building-line text-base md:text-2xl text-white"></i>
                  </div>
                  <div className="h-8 md:h-10 flex items-center justify-center mt-2 md:mt-3">
                    <p className="text-[10px] md:text-sm font-medium text-[#1E1E1E] text-center leading-tight">{partner.name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Detail Modal */}
      {selectedStory && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedStory(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#2A7A62]">故事详情</h3>
              <button 
                onClick={() => setSelectedStory(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F9F9F9] transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-[#6C6C6C]"></i>
              </button>
            </div>
            
            <div className="flex items-center gap-4 mb-4 pb-4 border-b border-[#E0E0E0]">
              <img
                src={selectedStory.avatar}
                alt={selectedStory.name}
                className="w-16 h-16 rounded-full object-cover border-2 border-[#2A7A62]"
              />
              <div>
                <h4 className="text-lg font-bold text-[#1E1E1E]">{selectedStory.name}</h4>
                <p className="text-sm text-[#6C6C6C]">{selectedStory.role}</p>
              </div>
            </div>
            
            <p className="text-base text-[#1E1E1E] leading-relaxed mb-4">{selectedStory.story}</p>
            
            <div className="flex items-center justify-between text-sm text-[#6C6C6C] pt-4 border-t border-[#E0E0E0]">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                {selectedStory.date}
              </span>
              <button 
                onClick={(e) => handleStoryLike(selectedStory.id, e)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all cursor-pointer ${
                  likedStories.includes(selectedStory.id)
                    ? 'bg-[#E06B3D]/10 text-[#E06B3D]'
                    : 'hover:bg-[#E06B3D]/10 hover:text-[#E06B3D]'
                }`}
              >
                <i className={`${likedStories.includes(selectedStory.id) ? 'ri-heart-fill' : 'ri-heart-line'} text-lg`}></i>
                <span>{likedStories.includes(selectedStory.id) ? '已点赞' : '点赞'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProjectPage;
